import { useState } from 'react';
import type { ClubAPI } from '@shared/types/api';
import { parseYouTubeId, youTubeEmbedUrl, youTubeWatchUrl } from '@shared/youtube';
import { MarkdownEditor } from '@/components/MarkdownEditor';
import { useToast } from '@/components/ux/Toast';
import { AdminDialog } from '../AdminDialog';
import { useCreateAdminClubPost, useUpdateAdminClubPost } from '../../api/useAdminClubs';

interface Props {
  clubId: string;
  /** Post to edit; omit to write a new admin post. */
  post?: ClubAPI.ClubPost;
  onClose: () => void;
}

/** Create or edit an Outclose post: title, rich text body, optional YouTube video, pin to the club page. */
export function ClubPostDialog({ clubId, post, onClose }: Props) {
  const toast = useToast();
  const createPost = useCreateAdminClubPost();
  const updatePost = useUpdateAdminClubPost();
  const [title, setTitle] = useState(post?.title ?? '');
  const [body, setBody] = useState(post?.body ?? '');
  const [pinned, setPinned] = useState(post ? !!post.pinnedAt : true);
  const [youtubeUrl, setYoutubeUrl] = useState(post?.youtubeVideoId ? youTubeWatchUrl(post.youtubeVideoId) : '');

  const videoId = youtubeUrl.trim() ? parseYouTubeId(youtubeUrl) : null;
  const videoInvalid = !!youtubeUrl.trim() && !videoId;
  const pending = createPost.isPending || updatePost.isPending;
  const canSave = !!title.trim() && !!body.trim() && !videoInvalid && !pending;
  const onError = (err: Error) => toast.error(err.message || 'Something went wrong');

  const save = () => {
    if (!canSave) return;
    const data = { title: title.trim(), body, pinned, youtubeUrl: videoId };
    if (post) {
      updatePost.mutate({ clubId, postId: post.id, data }, { onSuccess: () => { toast.success('Post updated'); onClose(); }, onError });
    } else {
      createPost.mutate({ clubId, data }, { onSuccess: () => { toast.success(pinned ? 'Post pinned to the club page' : 'Post published'); onClose(); }, onError });
    }
  };

  return (
    <AdminDialog
      open
      onClose={onClose}
      title={post ? 'Edit post' : 'New post'}
      size="lg"
      footer={
        <>
          <label className="flex items-center gap-2 text-sm mr-auto dark:text-neutral-200">
            <input type="checkbox" className="checkbox checkbox-sm" checked={pinned} onChange={(e) => setPinned(e.target.checked)} />
            Pin to club page
          </label>
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">Cancel</button>
          <button type="button" onClick={save} disabled={!canSave} className="btn btn-primary btn-sm">
            {post ? 'Save' : 'Publish'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={150}
          placeholder="Title"
          autoFocus
          className="w-full bg-transparent text-xl font-bold placeholder:text-neutral-400 focus:outline-none dark:text-neutral-100"
        />
        <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 p-3 min-h-[12rem]">
          <MarkdownEditor
            value={body}
            initialContent={post?.body}
            onChange={setBody}
            onSubmit={save}
            placeholder="This month we're reading…"
          />
        </div>
        <label className="block">
          <span className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">YouTube video (optional)</span>
          <input
            value={youtubeUrl}
            onChange={(e) => setYoutubeUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=…"
            className={`input input-bordered input-sm w-full ${videoInvalid ? 'input-error' : ''}`}
          />
          {videoInvalid && <span className="block text-xs text-red-500 mt-1">That doesn't look like a YouTube video link.</span>}
        </label>
        {videoId && (
          <div className="relative w-full aspect-video overflow-hidden rounded-xl bg-black">
            <iframe
              src={youTubeEmbedUrl(videoId)}
              title="Video preview"
              allow="encrypted-media; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full border-0"
            />
          </div>
        )}
      </div>
    </AdminDialog>
  );
}
